import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { DateTime } from 'luxon';
import { StateApiError, checkForBadHttpErrorMessage, checkToAddStateApiError, checkToRemoveStateApiError } from '@trovata/app/core/models/state-error.model';
import { StablecoinWalletsService } from '../../../services/wallets/wallets.service';
import { StablecoinBalance, StablecoinBalanceHistoryPoint, StablecoinWallet } from '../../../models/wallet.model';
import { StablecoinDepositInstruction } from '../../../models/deposit-instruction.model';
import { StablecoinAccountFunding } from '../../../models/account-funding.model';
import {
	ClearStablecoinWalletsState,
	CreateStablecoinDepositInstructions,
	DecideStablecoinAccountPaymentType,
	GetStablecoinAccountFunding,
	GetStablecoinBalanceHistory,
	GetStablecoinDepositInstructions,
	GetStablecoinWalletBalances,
	GetStablecoinWallets,
	InitStablecoinWalletsState,
	ResetStablecoinWalletsState,
	SetStablecoinAccountOnchainAccount,
	SetStablecoinAccountPaymentType,
} from '../../actions/wallets.actions';

export interface StablecoinWalletsStateModel {
	wallets: StablecoinWallet[] | null;
	balancesByWalletId: { [walletId: string]: StablecoinBalance[] };
	balanceHistory: StablecoinBalanceHistoryPoint[] | null;
	depositInstructionsByWalletId: { [walletId: string]: StablecoinDepositInstruction[] };
	apiErrors: StateApiError[];
	isCached: boolean;
	walletsLoading: boolean;
	balanceHistoryLoading: boolean;
	depositInstructionsLoading: boolean;
	/** True while a payment-type enable/disable/decision is in flight. */
	paymentTypeActionInFlight: boolean;
	/** Coinbase balance + deposit instructions, keyed by the account it was requested for. */
	fundingByAccountId: { [accountId: string]: StablecoinAccountFunding };
	/** True while an on-chain (sends-from) link change is in flight. */
	onchainLinkInFlight: boolean;
}

const DEFAULT_STABLECOIN_WALLETS_STATE: StablecoinWalletsStateModel = {
	wallets: null,
	balancesByWalletId: {},
	balanceHistory: null,
	depositInstructionsByWalletId: {},
	apiErrors: [],
	isCached: false,
	walletsLoading: true,
	balanceHistoryLoading: true,
	depositInstructionsLoading: false,
	paymentTypeActionInFlight: false,
	fundingByAccountId: {},
	onchainLinkInFlight: false,
};

@State<StablecoinWalletsStateModel>({
	name: 'stablecoinWallets',
	defaults: { ...DEFAULT_STABLECOIN_WALLETS_STATE },
})
@Injectable()
export class StablecoinWalletsState {
	private readonly walletsService: StablecoinWalletsService = inject(StablecoinWalletsService);

	@Selector()
	static wallets(state: StablecoinWalletsStateModel): StablecoinWallet[] | null {
		return state.wallets;
	}

	@Selector()
	static balancesByWalletId(state: StablecoinWalletsStateModel): { [walletId: string]: StablecoinBalance[] } {
		return state.balancesByWalletId;
	}

	@Selector()
	static balanceHistory(state: StablecoinWalletsStateModel): StablecoinBalanceHistoryPoint[] | null {
		return state.balanceHistory;
	}

	@Selector()
	static depositInstructionsByWalletId(state: StablecoinWalletsStateModel): { [walletId: string]: StablecoinDepositInstruction[] } {
		return state.depositInstructionsByWalletId;
	}

	@Selector()
	static walletsLoading(state: StablecoinWalletsStateModel): boolean {
		return state.walletsLoading;
	}

	@Selector()
	static balanceHistoryLoading(state: StablecoinWalletsStateModel): boolean {
		return state.balanceHistoryLoading;
	}

	@Selector()
	static depositInstructionsLoading(state: StablecoinWalletsStateModel): boolean {
		return state.depositInstructionsLoading;
	}

	@Selector()
	static paymentTypeActionInFlight(state: StablecoinWalletsStateModel): boolean {
		return state.paymentTypeActionInFlight;
	}

	@Selector()
	static fundingByAccountId(state: StablecoinWalletsStateModel): { [accountId: string]: StablecoinAccountFunding } {
		return state.fundingByAccountId;
	}

	@Selector()
	static onchainLinkInFlight(state: StablecoinWalletsStateModel): boolean {
		return state.onchainLinkInFlight;
	}

	@Selector()
	static apiErrors(state: StablecoinWalletsStateModel): StateApiError[] {
		return state.apiErrors;
	}

	@Action(InitStablecoinWalletsState)
	init(): void {
		// Fetches are dispatched lazily from page components (no entitlement gate yet).
	}

	@Action(ClearStablecoinWalletsState)
	clear(context: StateContext<StablecoinWalletsStateModel>): void {
		context.setState({ ...DEFAULT_STABLECOIN_WALLETS_STATE });
	}

	@Action(ResetStablecoinWalletsState)
	reset(context: StateContext<StablecoinWalletsStateModel>): void {
		context.setState({ ...DEFAULT_STABLECOIN_WALLETS_STATE });
	}

	@Action(GetStablecoinWallets)
	getWallets(context: StateContext<StablecoinWalletsStateModel>, action: GetStablecoinWallets): Observable<StablecoinWallet[]> | void {
		const state: StablecoinWalletsStateModel = context.getState();
		if (state.isCached && !action.refresh) {
			return;
		}
		context.patchState({ walletsLoading: true });
		return this.walletsService.getWallets().pipe(
			tap((wallets: StablecoinWallet[]) => {
				checkToRemoveStateApiError(context, GetStablecoinWallets);
				context.patchState({ wallets, isCached: true, walletsLoading: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ walletsLoading: false });
				checkToAddStateApiError(error, context, GetStablecoinWallets, checkForBadHttpErrorMessage(error, 'Unable to load wallets.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(GetStablecoinWalletBalances)
	getWalletBalances(context: StateContext<StablecoinWalletsStateModel>, action: GetStablecoinWalletBalances): Observable<StablecoinBalance[]> {
		return this.walletsService.getWalletBalances(action.accountId).pipe(
			tap((balances: StablecoinBalance[]) => {
				checkToRemoveStateApiError(context, GetStablecoinWalletBalances);
				const balancesByWalletId: { [walletId: string]: StablecoinBalance[] } = {
					...context.getState().balancesByWalletId,
					[action.accountId]: balances,
				};
				context.patchState({ balancesByWalletId });
			}),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, GetStablecoinWalletBalances, checkForBadHttpErrorMessage(error, 'Unable to load wallet balances.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(GetStablecoinBalanceHistory)
	getBalanceHistory(context: StateContext<StablecoinWalletsStateModel>, action: GetStablecoinBalanceHistory): Observable<StablecoinBalanceHistoryPoint[]> {
		context.patchState({ balanceHistoryLoading: true });
		const toDate: string = DateTime.now().toISODate();
		const fromDate: string = DateTime.now().minus({ days: action.days }).toISODate();
		return this.walletsService.getBalanceHistory(fromDate, toDate, action.accountId).pipe(
			tap((balanceHistory: StablecoinBalanceHistoryPoint[]) => {
				checkToRemoveStateApiError(context, GetStablecoinBalanceHistory);
				context.patchState({ balanceHistory, balanceHistoryLoading: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ balanceHistoryLoading: false });
				checkToAddStateApiError(error, context, GetStablecoinBalanceHistory, checkForBadHttpErrorMessage(error, 'Unable to load balance history.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(GetStablecoinDepositInstructions)
	getDepositInstructions(
		context: StateContext<StablecoinWalletsStateModel>,
		action: GetStablecoinDepositInstructions
	): Observable<StablecoinDepositInstruction[]> {
		context.patchState({ depositInstructionsLoading: true });
		return this.walletsService.getDepositInstructions(action.accountId).pipe(
			tap((instructions: StablecoinDepositInstruction[]) => {
				checkToRemoveStateApiError(context, GetStablecoinDepositInstructions);
				const depositInstructionsByWalletId: { [walletId: string]: StablecoinDepositInstruction[] } = {
					...context.getState().depositInstructionsByWalletId,
					[action.accountId]: instructions,
				};
				context.patchState({ depositInstructionsByWalletId, depositInstructionsLoading: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ depositInstructionsLoading: false });
				checkToAddStateApiError(
					error,
					context,
					GetStablecoinDepositInstructions,
					checkForBadHttpErrorMessage(error, 'Unable to load funding instructions.').message
				);
				return throwError(() => error);
			})
		);
	}

	@Action(CreateStablecoinDepositInstructions)
	createDepositInstructions(
		context: StateContext<StablecoinWalletsStateModel>,
		action: CreateStablecoinDepositInstructions
	): Observable<StablecoinDepositInstruction> {
		context.patchState({ depositInstructionsLoading: true });
		return this.walletsService.createDepositInstructions(action.body).pipe(
			tap((instruction: StablecoinDepositInstruction) => {
				checkToRemoveStateApiError(context, CreateStablecoinDepositInstructions);
				const state: StablecoinWalletsStateModel = context.getState();
				const existing: StablecoinDepositInstruction[] = state.depositInstructionsByWalletId[action.body.account_id] ?? [];
				const withoutDuplicate: StablecoinDepositInstruction[] = existing.filter((item: StablecoinDepositInstruction) => item.id !== instruction.id);
				context.patchState({
					depositInstructionsByWalletId: {
						...state.depositInstructionsByWalletId,
						[action.body.account_id]: [instruction, ...withoutDuplicate],
					},
					depositInstructionsLoading: false,
				});
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ depositInstructionsLoading: false });
				checkToAddStateApiError(
					error,
					context,
					CreateStablecoinDepositInstructions,
					checkForBadHttpErrorMessage(error, 'Unable to create funding instructions.').message
				);
				return throwError(() => error);
			})
		);
	}

	@Action(SetStablecoinAccountPaymentType)
	setAccountPaymentType(context: StateContext<StablecoinWalletsStateModel>, action: SetStablecoinAccountPaymentType): Observable<StablecoinWallet> {
		context.patchState({ paymentTypeActionInFlight: true });
		return this.walletsService.setPaymentTypeEnabled(action.accountId, action.paymentType, action.enabled).pipe(
			tap((wallet: StablecoinWallet) => {
				checkToRemoveStateApiError(context, SetStablecoinAccountPaymentType);
				this.replaceWallet(context, wallet);
				context.patchState({ paymentTypeActionInFlight: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ paymentTypeActionInFlight: false });
				checkToAddStateApiError(
					error,
					context,
					SetStablecoinAccountPaymentType,
					checkForBadHttpErrorMessage(error, 'Unable to change the payment type.').message
				);
				return throwError(() => error);
			})
		);
	}

	@Action(DecideStablecoinAccountPaymentType)
	decideAccountPaymentType(context: StateContext<StablecoinWalletsStateModel>, action: DecideStablecoinAccountPaymentType): Observable<StablecoinWallet> {
		context.patchState({ paymentTypeActionInFlight: true });
		return this.walletsService.decidePaymentType(action.accountId, action.paymentType, action.decision).pipe(
			tap((wallet: StablecoinWallet) => {
				checkToRemoveStateApiError(context, DecideStablecoinAccountPaymentType);
				this.replaceWallet(context, wallet);
				context.patchState({ paymentTypeActionInFlight: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ paymentTypeActionInFlight: false });
				checkToAddStateApiError(
					error,
					context,
					DecideStablecoinAccountPaymentType,
					checkForBadHttpErrorMessage(error, 'Unable to record the decision.').message
				);
				return throwError(() => error);
			})
		);
	}

	@Action(SetStablecoinAccountOnchainAccount)
	setOnchainAccount(context: StateContext<StablecoinWalletsStateModel>, action: SetStablecoinAccountOnchainAccount): Observable<StablecoinWallet> {
		context.patchState({ onchainLinkInFlight: true });
		return this.walletsService.setOnchainAccount(action.accountId, action.onchainAccountId).pipe(
			tap((wallet: StablecoinWallet) => {
				checkToRemoveStateApiError(context, SetStablecoinAccountOnchainAccount);
				this.replaceWallet(context, wallet);
				// The account's funding now resolves to a different (or no) Coinbase account.
				const fundingByAccountId: { [accountId: string]: StablecoinAccountFunding } = { ...context.getState().fundingByAccountId };
				delete fundingByAccountId[action.accountId];
				context.patchState({ fundingByAccountId, onchainLinkInFlight: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ onchainLinkInFlight: false });
				checkToAddStateApiError(
					error,
					context,
					SetStablecoinAccountOnchainAccount,
					checkForBadHttpErrorMessage(error, 'Unable to change where on-chain payments send from.').message
				);
				return throwError(() => error);
			})
		);
	}

	@Action(GetStablecoinAccountFunding)
	getAccountFunding(context: StateContext<StablecoinWalletsStateModel>, action: GetStablecoinAccountFunding): Observable<StablecoinAccountFunding> {
		return this.walletsService.getAccountFunding(action.accountId).pipe(
			tap((funding: StablecoinAccountFunding) => {
				checkToRemoveStateApiError(context, GetStablecoinAccountFunding);
				context.patchState({ fundingByAccountId: { ...context.getState().fundingByAccountId, [action.accountId]: funding } });
			}),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, GetStablecoinAccountFunding, checkForBadHttpErrorMessage(error, 'Unable to load the account balance.').message);
				return throwError(() => error);
			})
		);
	}

	private replaceWallet(context: StateContext<StablecoinWalletsStateModel>, wallet: StablecoinWallet): void {
		const wallets: StablecoinWallet[] | null = context.getState().wallets;
		context.patchState({ wallets: wallets?.map((item: StablecoinWallet) => (item.id === wallet.id ? wallet : item)) ?? null });
	}
}
